function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { GlyphCircle } from "@visx/glyph";
import { scaleLinear } from "@visx/scale";
import { interpolateNumber as d3InterpolateNumber } from "d3-interpolate";
import { observer } from "mobx-react";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import Glyphs from "./Glyphs";
import { jsx as _jsx } from "react/jsx-runtime";
const _MomentPointsChart = function MomentPointsChart(props, ref) {
  var _Glyphs;
  const {
    id,
    chartItem,
    basisItem,
    basisItemScales,
    scales,
    glyph = "circle"
  } = props;
  const points = useMemo(() => {
    if (basisItem && basisItemScales) {
      // We want to stick the chartItem points to the basis item, to do this we
      // interpolate the chart item points to match the basis item points. This
      // interpolation should not affect the scale of the chart item points.
      const basisToSourceScale = scaleLinear({
        domain: basisItemScales.y.domain(),
        range: scales.y.domain()
      });
      const interpolatedPoints = chartItem.points.map(p => _objectSpread(_objectSpread({}, p), interpolate(p, basisItem.points, basisToSourceScale)));
      return interpolatedPoints;
    }
    return chartItem.points;
  }, [chartItem, basisItem, basisItemScales, scales]);

  // Allow BottomDockChart and others to control zoom
  useImperativeHandle(ref, () => ({
    doZoom(scales) {
      if (points.length === 0) {
        return;
      }
      const vGlyphs = document.querySelectorAll("g#".concat(id, " > g.visx-glyph"));
      vGlyphs.forEach((vGlyph, i) => {
        const point = points[i];
        if (point) {
          const left = scales.x(point.x);
          const top = scales.y(point.y);
          const scale = point.isSelected ? "scale(1.4, 1.4)" : "";
          vGlyph.setAttribute("transform", "translate(".concat(left, ", ").concat(top, ") ").concat(scale));
          vGlyph.setAttribute("fill-opacity", "".concat(point.isSelected ? 1.0 : 0.3));
        }
      });
    }
  }), [id, points]);
  const baseKey = "moment-point-".concat(chartItem.categoryName, "-").concat(chartItem.name);
  const fillColor = chartItem.getColor();
  const isClickable = chartItem.onClick !== undefined;
  const clickProps = point => {
    if (isClickable) {
      return {
        pointerEvents: "all",
        cursor: "pointer",
        onClick: () => chartItem.onClick(point)
      };
    }
    return {};
  };
  const Glyph = (_Glyphs = Glyphs[glyph]) !== null && _Glyphs !== void 0 ? _Glyphs : GlyphCircle;
  return /*#__PURE__*/_jsx("g", {
    id: id,
    children: points.map((p, i) => /*#__PURE__*/_jsx(Glyph, _objectSpread({
      left: scales.x(p.x),
      top: scales.y(p.y),
      size: 100,
      fill: fillColor,
      fillOpacity: p.isSelected ? 1.0 : 0.3
    }, clickProps(p)), "".concat(baseKey, "-").concat(i)))
  });
};

/** Interpolates the given source point {x, y} to the closet point in the `sortedPoints` array.
 *
 * The source point and `sortedBasisPoints` may be of different scale, so we use `basisToSourceScale`
 * to generate a point in the original source items scale.
 */
function interpolate(p, sortedBasisPoints, basisToSourceScale) {
  // MomentPointsChart always has Dates for x coordinates
  const x = p.x;
  const closest = closestPointIndex(x, sortedBasisPoints);
  if (closest === undefined) {
    return p;
  }
  const a = sortedBasisPoints[closest];
  const b = sortedBasisPoints[closest + 1];
  if (a === undefined || b === undefined) {
    return p;
  }
  const aTime = a.x.getTime();
  const bTime = b.x.getTime();
  const xAsPercentage = (x.getTime() - aTime) / (bTime - aTime);
  const interpolated = {
    x,
    y: d3InterpolateNumber(basisToSourceScale(a.y), basisToSourceScale(b.y))(xAsPercentage)
  };
  return interpolated;
}
function closestPointIndex(x, sortedPoints) {
  const xTime = x.getTime();
  const index = sortedPoints.findIndex(p => p.x.getTime() >= xTime);
  if (index === -1) {
    return undefined;
  }
  if (index === 0) {
    return 0;
  }
  return index - 1;
}
export default observer(/*#__PURE__*/forwardRef(_MomentPointsChart));